import {TypeChatSocket} from "@dropDesk/domain/entities/chat/chat_enum";
import {TicketEntity} from "@dropDesk/domain/entities/ticket/ticket.entity";
import {TicketMessageEntity} from "@dropDesk/domain/entities/ticket/message/ticket_message.entity";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {InternalChatEntity} from "@dropDesk/domain/entities/internal_chat/internal_chat_entity";
import {InternalChatMessageEntity} from "@dropDesk/domain/entities/internal_chat/messages/internal_chat_message_entity";

export class SocketResponseContent {
    idTicket?: string;
    ticket?: Record<string, any>;
    messages?: Array<Record<string, any>>;
    notification?: Record<string, any>;
    idInternalChat?: string;
    internalChat?: Record<string, any>;
    internalChatMessages?: Array<Record<string, any>>;

    constructor({
                    idTicket,
                    ticket,
                    messages,
                    notification,
                    idInternalChat,
                    internalChat,
                    internalChatMessages
                }: {
        idTicket?: string;
        ticket?: Record<string, any>;
        messages?: Array<Record<string, any>>;
        notification?: Record<string, any>;
        idInternalChat?: string;
        internalChat?: Record<string, any>;
        internalChatMessages?: Array<Record<string, any>>;
    }) {
        Object.assign(this, {
            idTicket,
            ticket,
            messages,
            notification,
            idInternalChat,
            internalChat,
            internalChatMessages
        });
    }
}

export class SocketResponse {
    type!: TypeChatSocket;
    rooms!: string[];
    idTicket?: string;
    tickets?: TicketEntity[] | null;
    messages?: TicketMessageEntity[] | null;
    notification?: Record<string, any>;
    idInternalChat?: string;
    internalChats?: InternalChatEntity[] | null;
    internalChatMessages?: InternalChatMessageEntity[] | null;

    constructor({
                    type,
                    rooms,
                    tickets,
                    messages,
                    idTicket,
                    notification,
                    idInternalChat,
                    internalChats,
                    internalChatMessages,
                }: {
        type: TypeChatSocket,
        rooms: string[];
        tickets?: TicketEntity[] | null;
        messages?: TicketMessageEntity[] | null;
        idTicket?: string;
        notification?: Record<string, any>;
        idInternalChat?: string;
        internalChats?: InternalChatEntity[] | null;
        internalChatMessages?: InternalChatMessageEntity[] | null;
    }) {
        Object.assign(this, {
            type,
            rooms,
            tickets,
            messages,
            idTicket,
            notification,
            idInternalChat,
            internalChats,
            internalChatMessages,
        });
    }

    public get hasUnknownError() {
        return this.type === TypeChatSocket.unknown;
    }

    public get isInformRooms() {
        return this.type === TypeChatSocket.informRooms;
    }

    public isEmptyData(user: UserEntity) {
        return this.eligibleMessages(user).length === 0 && !this.tickets && !this.notification && !this.idTicket;
    }

    public eligibleMessages(user: UserEntity): Array<TicketMessageEntity> {
        const allMessages = [
            ...(this.messages ?? []),
        ];
        return user.isAttendant
            ? allMessages
            : allMessages.filter((message) => !message.isPrivate);
    }

    public getTicketList(): Array<string> {
        return [
            ...new Set([
                ...(this.tickets?.map(entry => entry.id) ?? []),
                ...(this.messages?.map(entry => entry.idTicket) ?? []),
                ...(this.idTicket ? [this.idTicket] : [])
            ])
        ]
    }

    public getInternalChatList(): Array<string> {
        return [
            ...new Set([
                ...(this.internalChats?.map(entry => entry.id) ?? []),
                ...(this.internalChatMessages?.map(entry => entry.idInternalChat) ?? []),
                ...(this.idInternalChat ? [this.idInternalChat] : [])
            ])
        ]
    }

    public isEmptyDataInternalCha() {
        return (!this.internalChatMessages || this.internalChatMessages.length === 0) && !this.internalChats && !this.idInternalChat;
    }

    public static fromJson(json: SocketResponseEntity): SocketResponse {
        return new SocketResponse({
            type: json.type,
            rooms: json.type === TypeChatSocket.informRooms && json.rooms ? json.rooms : [],
            messages: json.data?.messages && json.data?.messages.length > 0 ?
                json.data!.messages.map((entry: any) => TicketMessageEntity.fromJson(entry)) : null,
            notification: json.data?.notification,

            idTicket: json.data?.idTicket ?? json.data?.notification?.idTicket,
            tickets: json.type === TypeChatSocket.ticket && json.data?.ticket ? [TicketEntity.fromJson(json.data!.ticket)] : null,

            idInternalChat: json.data?.idInternalChat ?? json.data?.notification?.idInternalChat,
            internalChats: json.type === TypeChatSocket.internalChat && json.data?.internalChat ? [InternalChatEntity.fromJson(json.data!.internalChat)] : null,
            internalChatMessages: json.data?.internalChatMessages && json.data?.internalChatMessages.length > 0 ?
                json.data!.internalChatMessages.map((entry: any) => InternalChatMessageEntity.fromJson(entry)) : null,
        });
    }
}


export class SocketResponseEntity {
    type!: TypeChatSocket;
    data?: SocketResponseContent;
    rooms?: Array<string>;
    error?: string;

    constructor({
                    type,
                    data,
                    rooms,
                    error,
                }: {
        type: TypeChatSocket;
        data?: SocketResponseContent;
        rooms?: Array<string>;
        error?: string;
    }) {
        Object.assign(this, {
            type,
            data,
            rooms,
            error,
        });
    }
}
